import { useTranslation } from 'react-i18next';
import { Circle, CircleCheck, Target } from 'lucide-react';
import { climbStanding, type ClimbStanding, type ClimbStandingSource } from './climb-standing';

// One glyph per standing, on a circle throughout so the five read as one
// progression rather than five unrelated pictures: an empty ring to fill, a
// target being aimed at, a ring with a tick in it. The three sends differ only
// by colour — the shape says "done", the colour says how.
const STANDING_GLYPHS: Record<ClimbStanding, { Icon: typeof Circle; className: string }> = {
  to_try: { Icon: Circle, className: 'text-muted-foreground/50' },
  attempted: { Icon: Target, className: 'text-orange-500' },
  on_sight: { Icon: CircleCheck, className: 'text-red-500' },
  flash: { Icon: CircleCheck, className: 'text-green-600' },
  sent: { Icon: CircleCheck, className: 'text-blue-500' },
};

interface ClimbStandingIconProps {
  climb: ClimbStandingSource;
  /** Sizing and spacing; the colour is the state's own and is not overridable. */
  className?: string;
}

/**
 * Where the reader stands on a climb, as a single icon — the wordless version
 * of "to try / 3 attempts / flash". Meant for lists, where a badge of text on
 * every row was more noise than news; the state is still spelled out in the
 * tooltip and to screen readers.
 *
 * Only meaningful for a signed-in reader whose rows carry `user_topped`,
 * `user_send_type` and `user_attempts` (a list asked for with
 * `include_user_attempts`): the caller hides it otherwise. The state itself
 * comes from `climbStanding` (climb-standing.ts).
 */
export function ClimbStandingIcon({ climb, className = 'w-5 h-5' }: ClimbStandingIconProps) {
  const { t } = useTranslation();
  const standing = climbStanding(climb);
  const { Icon, className: colour } = STANDING_GLYPHS[standing];
  const label = t(`climbStanding.${standing}`);

  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      data-testid="climb-standing-icon"
      data-standing={standing}
      className="inline-flex shrink-0 items-center"
    >
      <Icon className={`${className} ${colour}`} aria-hidden />
    </span>
  );
}
